import { fetchCustomerCatalog } from '../utils/customerRegistryRequests';
import { useCustomerRegistryRefresh } from '../utils/customerRegistryEvents';
import { resolveCrmCustomerDeals, crmCustomerChanges } from '../utils/crmCustomerRegistry';
import React, { useEffect, useMemo, useState } from 'react';
import AIInsightsPanel from '../components/AIInsightsPanel';
import {
  FaHandshake,
  FaUsers,
  FaChartLine,
  FaCalendarCheck,
  FaMoneyBillWave,
  FaFilter,
  FaSearch,
  FaPlus,
  FaUserTie,
  FaStickyNote,
  FaCheckCircle,
  FaClock,
  FaTrash,
  FaLinkedin,
  FaInstagram,
  FaFacebook,
  FaWhatsapp,
  FaShareAlt,
  FaEdit,
  FaExternalLinkAlt,
  FaLayerGroup,
  FaTrophy,
  FaExclamationTriangle,
  FaChartPie,
  FaBullseye,
  FaInfoCircle,
  FaPlug,
  FaLightbulb,
  FaEnvelope,
  FaComments,
  FaSyncAlt,
  FaTimes,
} from 'react-icons/fa';
import Modal from '../components/Modal';
import CRMEmailMarketing from '../components/CRMEmailMarketing';
import CRMChannelInbox from '../components/CRMChannelInbox';
import api from '../api';

const PIPELINE_STAGES = [
  {
    id: 'new',
    label: 'Novos Leads',
    description: '',
    badgeClass: 'bg-sky-100 text-sky-600',
    borderClass: 'border-sky-200',
    progressClass: 'bg-sky-400',
    defaultProbability: 0.18,
  },
  {
    id: 'qualification',
    label: 'Qualificação',
    description: '',
    badgeClass: 'bg-blue-100 text-blue-600',
    borderClass: 'border-blue-200',
    progressClass: 'bg-blue-500',
    defaultProbability: 0.32,
  },
  {
    id: 'proposal',
    label: 'Proposta',
    description: '',
    badgeClass: 'bg-indigo-100 text-indigo-600',
    borderClass: 'border-indigo-200',
    progressClass: 'bg-indigo-500',
    defaultProbability: 0.55,
  },
  {
    id: 'negotiation',
    label: 'Negociação',
    description: '',
    badgeClass: 'bg-purple-100 text-purple-600',
    borderClass: 'border-purple-200',
    progressClass: 'bg-purple-500',
    defaultProbability: 0.72,
  },
  {
    id: 'won',
    label: 'Ganhos recentes',
    description: '',
    badgeClass: 'bg-emerald-100 text-emerald-600',
    borderClass: 'border-emerald-200',
    progressClass: 'bg-emerald-500',
    defaultProbability: 1,
  },
];

const PIPELINE_STAGE_MAP = PIPELINE_STAGES.reduce((acc, stage) => {
  acc[stage.id] = stage;
  return acc;
}, {});

const CRM_TABS = [
  { id: 'pipeline', label: 'Funil', icon: FaLayerGroup, hint: 'Kanban para mover oportunidades entre etapas.' },
  { id: 'overview', label: 'Resumo', icon: FaChartPie, hint: 'Indicadores essenciais do relacionamento comercial.' },
  { id: 'accounts', label: 'Contas', icon: FaUsers, hint: 'Empresas, responsáveis e agenda.' },
  { id: 'integrations', label: 'Canais', icon: FaPlug, hint: 'Redes e automações conectadas.' },
  { id: 'inbox', label: 'Mensagens', icon: FaComments, hint: 'Mensagens recebidas e respostas dos canais conectados.' },
  { id: 'emailMarketing', label: 'E-mail Marketing', icon: FaEnvelope, hint: 'Campanhas para os contatos do CRM.' },
  { id: 'reports', label: 'Insights', icon: FaLightbulb, hint: 'Alertas e previsões comerciais.' },
];


const CRM_STORAGE_KEY = 'bpm.crm.deals.v2';

const loadStoredDeals = () => {
  if (typeof window === 'undefined') return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(CRM_STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

function InfoHint({ label }) {
  if (!label) return null;
  return (
    <span className="group relative inline-flex">
      <FaInfoCircle className="text-slate-400 transition group-hover:text-sky-500" aria-label={label} />
      <span className="pointer-events-none absolute right-0 top-6 z-20 hidden w-56 rounded-2xl bg-slate-950 px-3 py-2 text-xs font-medium leading-relaxed text-white shadow-xl group-hover:block">
        {label}
      </span>
    </span>
  );
}

const DEFAULT_STAGE_ID = PIPELINE_STAGES[0].id;

const SEGMENT_OPTIONS = ['Serviços', 'Tecnologia', 'Indústria', 'Varejo', 'Saúde'];

const getDefaultProbability = (stageId) =>
  PIPELINE_STAGE_MAP[stageId]?.defaultProbability ??
  PIPELINE_STAGE_MAP[DEFAULT_STAGE_ID].defaultProbability;

const SOCIAL_INTEGRATION_OPTIONS = [
  {
    id: 'linkedin',
    name: 'LinkedIn',
    icon: FaLinkedin,
    description: 'Integração comercial do LinkedIn sem acesso padrão às mensagens privadas.',
    audience: 'A API padrão não permite centralizar Direct Messages nesta caixa de entrada.',
    messagingSupported: false,
    loginUrl: 'https://www.linkedin.com/login',
    documentationUrl: 'https://www.linkedin.com/help/linkedin/answer/a507663',
  },
  {
    id: 'instagram',
    name: 'Instagram',
    icon: FaInstagram,
    description: 'Monitore respostas de stories e directs para engajar leads mais quentes.',
    audience: 'Fortaleça a presença de marca e responda rapidamente a interações.',
    loginUrl: 'https://www.instagram.com/accounts/login/',
    documentationUrl: 'https://developers.facebook.com/docs/instagram-platform/',
    messagingSupported: true,
  },
  {
    id: 'facebook',
    name: 'Facebook',
    icon: FaFacebook,
    description: 'Sincronize formulários de anúncios e páginas para nutrir o pipeline.',
    audience: 'Centralize contatos captados em campanhas e comunidades.',
    loginUrl: 'https://www.facebook.com/login.php',
    documentationUrl: 'https://developers.facebook.com/docs/messenger-platform/',
    messagingSupported: true,
  },
  {
    id: 'whatsapp',
    name: 'WhatsApp Business',
    icon: FaWhatsapp,
    description: 'Integre conversas para registrar compromissos e histórico com clientes.',
    audience: 'Acompanhe tratativas de vendas consultivas em tempo real.',
    loginUrl: 'https://www.facebook.com/business/help/205587591547335',
    documentationUrl: 'https://developers.facebook.com/docs/whatsapp/cloud-api/',
    messagingSupported: true,
  },
];

const EMPTY_FORM = {
  name: '',
  company: '', customerId: '',
  email: '',
  segment: SEGMENT_OPTIONS[0],
  owner: '',
  value: '',
  stage: DEFAULT_STAGE_ID,
  nextStep: '',
  nextStepDate: '',
  notes: '',
  cycleDays: '',
};

const EMPTY_INTEGRATION_FORM = {
  accountName: '',
  loginEmail: '',
  externalAccountId: '',
  accessToken: '',
  webhookSecret: '',
};



const formatCurrency = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(numeric);
};

const formatDate = (value) => {
  if (!value) return 'Sem agenda';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sem agenda';
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
};

const formatDateTime = (value) => {
  if (!value) return 'Sem registro';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sem registro';
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const isWithinDays = (value, days) => {
  if (!value) return false;
  const target = new Date(value);
  if (Number.isNaN(target.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  const diff = (target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);
  return diff >= 0 && diff <= days;
};

const getDaysDiff = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const today = new Date();
  const diff = (today.getTime() - date.getTime()) / (1000 * 60 * 60 * 24);
  return Math.floor(diff);
};

const formatDaysAgo = (value) => {
  const diff = getDaysDiff(value);
  if (diff === null) return 'Sem registro';
  if (diff === 0) return 'Hoje';
  if (diff === 1) return 'Há 1 dia';
  return `Há ${diff} dias`;
};

const getQuarterLabel = (date) => {
  const quarter = Math.floor(date.getMonth() / 3) + 1;
  return `T${quarter} ${date.getFullYear()}`;
};

export default function CRM({ openTab, initialTab, initialTabRequest }) {
  const [storedDeals, setDeals] = useState(loadStoredDeals);
  const [registryCustomers, setRegistryCustomers] = useState([]);
  const [dealError, setDealError] = useState('');
  const [savingDeal, setSavingDeal] = useState(false);
  const deals = useMemo(()=>resolveCrmCustomerDeals(storedDeals,registryCustomers),[storedDeals,registryCustomers]);
  const loadRegistryCustomers = async () => {
    try {
      const customers = await fetchCustomerCatalog(api);
      setRegistryCustomers(customers);
    } catch { /* Keep the currently loaded registry if a refresh fails. */ }
  };
  useEffect(()=>{loadRegistryCustomers()},[]);
  useCustomerRegistryRefresh(kind=>{if(kind!=='templates')loadRegistryCustomers()});
  const [searchTerm, setSearchTerm] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [segmentFilter, setSegmentFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('pipeline');
  useEffect(() => { if (['pipeline', 'inbox', 'integrations'].includes(initialTab)) setActiveTab(initialTab); }, [initialTab, initialTabRequest]);
  const [draggedDealId, setDraggedDealId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(() => ({ ...EMPTY_FORM }));
  const [editingDealId, setEditingDealId] = useState(null);
  const [socialIntegrations, setSocialIntegrations] = useState(() =>
    SOCIAL_INTEGRATION_OPTIONS.map((integration) => ({
      ...integration,
      connected: false,
      lastSync: null,
      connectionDetails: null,
    })),
  );
  const [integrationModal, setIntegrationModal] = useState({ open: false, integrationId: null });
  const [integrationForm, setIntegrationForm] = useState(() => ({ ...EMPTY_INTEGRATION_FORM }));
  const [integrationBusy, setIntegrationBusy] = useState('');
  const [integrationError, setIntegrationError] = useState('');
  const [integrationNotice, setIntegrationNotice] = useState('');
  const [webhookConfiguration, setWebhookConfiguration] = useState(null);

  const loadChannelConnections = async (silent = false) => {
    if (!silent) setIntegrationBusy('load');
    try {
      const response = await api.get('/crm/channels/connections', {
        skipTransientRetry: silent,
      });
      const saved = Array.isArray(response.data?.connections)
        ? response.data.connections
        : [];
      const byProvider = new Map(saved.map((item) => [item.provider, item]));
      setSocialIntegrations(
        SOCIAL_INTEGRATION_OPTIONS.map((integration) => {
          const connection = byProvider.get(integration.id);
          return {
            ...integration,
            connected: Boolean(connection?.connected),
            status: connection?.status || 'DISCONNECTED',
            lastSync: connection?.lastSyncedAt || null,
            lastError: connection?.lastError || '',
            connectionDetails: connection
              ? {
                  accountName: connection.accountName || '',
                  loginEmail: connection.loginEmail || '',
                  externalAccountId: connection.externalAccountId || '',
                }
              : null,
          };
        }),
      );
      if (!silent) setIntegrationError('');
    } catch (err) {
      if (!silent) {
        setIntegrationError(
          err.response?.data?.error || 'Não foi possível carregar as conexões do CRM.',
        );
      }
    } finally {
      if (!silent) setIntegrationBusy('');
    }
  };

  useEffect(() => {
    loadChannelConnections();
    const timer = window.setInterval(() => loadChannelConnections(true), 30000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(CRM_STORAGE_KEY, JSON.stringify(deals));
    } catch {
      // O CRM continua funcionando mesmo sem persistência local disponível.
    }
  }, [deals]);

  const owners = useMemo(() => {
    const unique = new Set();
    deals.forEach((deal) => {
      if (deal.owner) {
        unique.add(deal.owner);
      }
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [deals]);

  const segments = useMemo(() => {
    const unique = new Set(SEGMENT_OPTIONS);
    deals.forEach((deal) => {
      if (deal.segment) {
        unique.add(deal.segment);
      }
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [deals]);

  const filteredDeals = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return deals.filter((deal) => {
      if (term) {
        const haystack = `${deal.name} ${deal.company} ${deal.owner} ${deal.email || ''}`.toLowerCase();
        if (!haystack.includes(term)) {
          return false;
        }
      }
      if (ownerFilter !== 'all' && deal.owner !== ownerFilter) {
        return false;
      }
      if (segmentFilter !== 'all' && deal.segment !== segmentFilter) {
        return false;
      }
      return true;
    });
  }, [deals, ownerFilter, searchTerm, segmentFilter]);

  const pipelineColumns = useMemo(
    () =>
      PIPELINE_STAGES.map((stage) => {
        const stageDeals = filteredDeals
          .filter((deal) => deal.stage === stage.id)
          .sort((a, b) => {
            if (!a.nextStepDate) return 1;
            if (!b.nextStepDate) return -1;
            return new Date(a.nextStepDate) - new Date(b.nextStepDate);
          });
        const totalValue = stageDeals.reduce((sum, deal) => sum + Number(deal.value || 0), 0);
        return {
          ...stage,
          deals: stageDeals,
          totalValue,
        };
      }),
    [filteredDeals],
  );

  const activeDealsCount = useMemo(
    () => filteredDeals.filter((deal) => deal.stage !== 'won').length,
    [filteredDeals],
  );

  const pipelineValue = useMemo(
    () =>
      filteredDeals
        .filter((deal) => deal.stage !== 'won')
        .reduce((sum, deal) => sum + Number(deal.value || 0), 0),
    [filteredDeals],
  );

  const weightedPipelineValue = useMemo(
    () =>
      filteredDeals
        .filter((deal) => deal.stage !== 'won')
        .reduce(
          (sum, deal) =>
            sum + Number(deal.value || 0) * (deal.probability ?? getDefaultProbability(deal.stage)),
          0,
        ),
    [filteredDeals],
  );

  const wonDealsValue = useMemo(
    () => deals.filter((deal) => deal.stage === 'won').reduce((sum, deal) => sum + Number(deal.value || 0), 0),
    [deals],
  );

  const wonRatio = useMemo(() => {
    const total = deals.length;
    if (!total) return 0;
    const wonCount = deals.filter((deal) => deal.stage === 'won').length;
    return Math.round((wonCount / total) * 100);
  }, [deals]);

  const averageCycle = useMemo(() => {
    const cycles = deals
      .map((deal) => Number(deal.cycleDays))
      .filter((value) => Number.isFinite(value) && value > 0);
    if (!cycles.length) return null;
    return Math.round(cycles.reduce((sum, value) => sum + value, 0) / cycles.length);
  }, [deals]);

  const dealsCreatedThisMonth = useMemo(() => {
    const today = new Date();
    const month = today.getMonth();
    const year = today.getFullYear();
    return deals.filter((deal) => {
      if (!deal.createdAt) return false;
      const created = new Date(deal.createdAt);
      return created.getMonth() === month && created.getFullYear() === year;
    }).length;
  }, [deals]);

  const wonThisMonthValue = useMemo(() => {
    const today = new Date();
    const month = today.getMonth();
    const year = today.getFullYear();
    return deals
      .filter((deal) => deal.stage === 'won' && deal.updatedAt)
      .filter((deal) => {
        const updated = new Date(deal.updatedAt);
        return updated.getMonth() === month && updated.getFullYear() === year;
      })
      .reduce((sum, deal) => sum + Number(deal.value || 0), 0);
  }, [deals]);

  const upcomingActivities = useMemo(
    () =>
      filteredDeals
        .filter((deal) => deal.stage !== 'won' && isWithinDays(deal.nextStepDate, 7))
        .sort((a, b) => new Date(a.nextStepDate) - new Date(b.nextStepDate)),
    [filteredDeals],
  );

  const focusDeals = useMemo(
    () =>
      filteredDeals
        .filter((deal) => deal.stage !== 'won')
        .sort((a, b) => b.probability - a.probability || b.value - a.value)
        .slice(0, 4),
    [filteredDeals],
  );

  const recentWins = useMemo(
    () =>
      deals
        .filter((deal) => deal.stage === 'won')
        .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0))
        .slice(0, 4),
    [deals],
  );

  const staleDeals = useMemo(() => {
    const today = new Date();
    return filteredDeals
      .filter((deal) => {
        if (deal.stage === 'won') return false;
        if (!deal.updatedAt) return true;
        const updated = new Date(deal.updatedAt);
        if (Number.isNaN(updated.getTime())) return false;
        const diff = (today.getTime() - updated.getTime()) / (1000 * 60 * 60 * 24);
        return diff >= 14;
      })
      .sort((a, b) => new Date(a.updatedAt || 0) - new Date(b.updatedAt || 0))
      .slice(0, 5);
  }, [filteredDeals]);

  const connectedIntegrations = useMemo(
    () => socialIntegrations.filter((integration) => integration.connected),
    [socialIntegrations],
  );

  const pipelineStageHealth = useMemo(
    () =>
      pipelineColumns.map((column) => {
        const withoutNextStep = column.deals.filter((deal) => !deal.nextStep).length;
        const overdue = column.deals.filter((deal) => {
          if (!deal.nextStepDate) return false;
          const next = new Date(deal.nextStepDate);
          if (Number.isNaN(next.getTime())) return false;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          next.setHours(0, 0, 0, 0);
          return next < today;
        }).length;
        const avgProbability = column.deals.length
          ? Math.round(
              (column.deals.reduce(
                (sum, deal) => sum + (deal.probability ?? getDefaultProbability(deal.stage)),
                0,
              ) /
                column.deals.length) *
                100,
            )
          : 0;
        return {
          id: column.id,
          label: column.label,
          count: column.deals.length,
          totalValue: column.totalValue,
          overdue,
          withoutNextStep,
          avgProbability,
        };
      }),
    [pipelineColumns],
  );

  const stageAlerts = useMemo(
    () => pipelineStageHealth.filter((stage) => stage.overdue > 0 || stage.withoutNextStep > 0),
    [pipelineStageHealth],
  );

  const accountSummaries = useMemo(() => {
    const map = new Map();
    filteredDeals.forEach((deal) => {
      const key = deal.company || 'Conta sem nome';
      if (!map.has(key)) {
        map.set(key, {
          company: key,
          opportunities: 0,
          totalValue: 0,
          owners: new Set(),
          nextStepDate: null,
          latestStage: deal.stage,
          segment: deal.segment || 'Não informado',
          won: 0,
        });
      }
      const entry = map.get(key);
      entry.opportunities += 1;
      entry.totalValue += Number(deal.value || 0);
      if (deal.owner) {
        entry.owners.add(deal.owner);
      }
      if (deal.stage === 'won') {
        entry.won += 1;
      }
      if (deal.nextStepDate) {
        const current = entry.nextStepDate ? new Date(entry.nextStepDate) : null;
        const next = new Date(deal.nextStepDate);
        if (!current || (Number.isFinite(next.getTime()) && next < current)) {
          entry.nextStepDate = deal.nextStepDate;
        }
      }
      entry.latestStage = deal.stage;
    });

    return Array.from(map.values())
      .map((entry) => ({
        ...entry,
        owners: Array.from(entry.owners).sort((a, b) => a.localeCompare(b, 'pt-BR')),
        winRate: entry.opportunities ? Math.round((entry.won / entry.opportunities) * 100) : 0,
      }))
      .sort((a, b) => b.totalValue - a.totalValue)
      .slice(0, 8);
  }, [filteredDeals]);

  const teamPerformance = useMemo(() => {
    const map = new Map();
    filteredDeals.forEach((deal) => {
      const owner = deal.owner || 'Sem responsável';
      if (!map.has(owner)) {
        map.set(owner, {
          owner,
          opportunities: 0,
          totalValue: 0,
          won: 0,
          wonValue: 0,
          cycles: [],
          upcoming: 0,
        });
      }
      const entry = map.get(owner);
      entry.opportunities += 1;
      entry.totalValue += Number(deal.value || 0);
      if (deal.stage === 'won') {
        entry.won += 1;
        entry.wonValue += Number(deal.value || 0);
      }
      if (deal.cycleDays) {
        entry.cycles.push(Number(deal.cycleDays));
      }
      if (isWithinDays(deal.nextStepDate, 7)) {
        entry.upcoming += 1;
      }
    });

    return Array.from(map.values())
      .map((entry) => ({
        ...entry,
        winRate: entry.opportunities ? Math.round((entry.won / entry.opportunities) * 100) : 0,
        avgCycle: entry.cycles.length
          ? Math.round(entry.cycles.reduce((sum, value) => sum + value, 0) / entry.cycles.length)
          : null,
      }))
      .sort((a, b) => b.totalValue - a.totalValue);
  }, [filteredDeals]);

  const coverageBySegment = useMemo(() => {
    const map = new Map();
    filteredDeals.forEach((deal) => {
      const segment = deal.segment || 'Sem segmento';
      if (!map.has(segment)) {
        map.set(segment, { segment, count: 0, value: 0 });
      }
      const entry = map.get(segment);
      entry.count += 1;
      entry.value += Number(deal.value || 0);
    });
    return Array.from(map.values()).sort((a, b) => b.value - a.value);
  }, [filteredDeals]);

  const forecastByQuarter = useMemo(() => {
    const map = new Map();
    filteredDeals
      .filter((deal) => deal.stage !== 'won')
      .forEach((deal) => {
        let key = 'Sem previsão';
        if (deal.nextStepDate) {
          const date = new Date(deal.nextStepDate);
          if (!Number.isNaN(date.getTime())) {
            key = getQuarterLabel(date);
          }
        }
        if (!map.has(key)) {
          map.set(key, { label: key, weighted: 0, total: 0, deals: 0 });
        }
        const entry = map.get(key);
        const probability = deal.probability ?? getDefaultProbability(deal.stage);
        entry.weighted += Number(deal.value || 0) * probability;
        entry.total += Number(deal.value || 0);
        entry.deals += 1;
      });
    return Array.from(map.values()).sort((a, b) => (a.label > b.label ? 1 : -1));
  }, [filteredDeals]);

  const summaryCards = useMemo(
    () => [
      {
        id: 'active',
        label: 'Oportunidades ativas',
        value: activeDealsCount,
        detail:
          pipelineColumns
            .filter((column) => column.deals.length)
            .map((column) => `${column.deals.length} em ${column.label}`)
            .join(' • ') || 'Nenhuma oportunidade em andamento',
        info: 'Distribuição das oportunidades abertas por etapa do funil.',
        icon: <FaHandshake className="text-sky-500" />,
        accent: 'from-sky-50 to-white',
      },
      {
        id: 'value',
        label: 'Valor em negociação',
        value: formatCurrency(pipelineValue),
        detail: `Previsto: ${formatCurrency(weightedPipelineValue)}`,
        info: 'Valor total em aberto e previsão ponderada pela probabilidade de fechamento.',
        icon: <FaMoneyBillWave className="text-emerald-500" />,
        accent: 'from-emerald-50 to-white',
      },
      {
        id: 'conversion',
        label: 'Taxa de conversão',
        value: `${wonRatio}%`,
        detail: averageCycle ? `${averageCycle} dias` : 'Sem ciclo médio',
        info: 'Percentual de oportunidades ganhas e tempo médio até fechamento.',
        icon: <FaChartLine className="text-indigo-500" />,
        accent: 'from-indigo-50 to-white',
      },
      {
        id: 'agenda',
        label: 'Próximos passos',
        value: upcomingActivities.length,
        detail: upcomingActivities.length > 0 ? '7 dias' : 'Em dia',
        info: 'Compromissos agendados para os próximos sete dias.',
        icon: <FaCalendarCheck className="text-purple-500" />,
        accent: 'from-purple-50 to-white',
      },
      {
        id: 'newDeals',
        label: 'Novas oportunidades (mês)',
        value: dealsCreatedThisMonth,
        detail: formatCurrency(wonThisMonthValue),
        info: 'Novas oportunidades e ganhos registrados no mês atual.',
        icon: <FaBullseye className="text-amber-500" />,
        accent: 'from-amber-50 to-white',
      },
    ],
    [
      activeDealsCount,
      averageCycle,
      dealsCreatedThisMonth,
      pipelineColumns,
      pipelineValue,
      upcomingActivities.length,
      weightedPipelineValue,
      wonRatio,
      wonThisMonthValue,
    ],
  );


  const moveDealToStage = (dealId, stageId) => {
    if (!dealId || !PIPELINE_STAGE_MAP[stageId]) {
      return;
    }

    setDeals((prev) =>
      prev.map((deal) => {
        if (deal.id !== dealId || deal.stage === stageId) {
          return deal;
        }
        return {
          ...deal,
          stage: stageId,
          probability: getDefaultProbability(stageId),
          updatedAt: new Date().toISOString(),
        };
      }),
    );
  };

  const handleDealDragStart = (event, dealId) => {
    setDraggedDealId(dealId);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', dealId);
  };

  const handleDealDragEnd = () => {
    setDraggedDealId(null);
  };

  const handleColumnDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const handleColumnDrop = (event, stageId) => {
    event.preventDefault();
    const dealId = event.dataTransfer.getData('text/plain') || draggedDealId;
    moveDealToStage(dealId, stageId);
    setDraggedDealId(null);
  };

  const handleStageChange = (dealId, stageId) => {
    moveDealToStage(dealId, stageId);
  };

  const openModal = (stageId = DEFAULT_STAGE_ID) => {
    setEditingDealId(null);
    setForm({ ...EMPTY_FORM, stage: PIPELINE_STAGE_MAP[stageId] ? stageId : DEFAULT_STAGE_ID });
    setModalOpen(true);
  };

  const openEditModal = (deal) => {
    setEditingDealId(deal.id);
    setForm({
      name: deal.name || '',
      company: deal.company || '', customerId: deal.customerId || '',
      email: deal.email || '',
      segment: deal.segment || SEGMENT_OPTIONS[0],
      owner: deal.owner || '',
      value: deal.value ?? '',
      stage: deal.stage || DEFAULT_STAGE_ID,
      nextStep: deal.nextStep || '',
      nextStepDate: deal.nextStepDate ? deal.nextStepDate.slice(0, 10) : '',
      notes: deal.notes || '',
      cycleDays: deal.cycleDays ?? '',
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setForm({ ...EMPTY_FORM });
    setEditingDealId(null);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedName = form.name.trim();
    const trimmedCompany = form.company.trim();
    if (!trimmedName || !trimmedCompany) {
      return;
    }

    const linkedCustomer=registryCustomers.find(customer=>String(customer.id)===String(form.customerId));
    if (linkedCustomer) {
      const changes=crmCustomerChanges(linkedCustomer,form);
      if (Object.keys(changes).length) {
        setSavingDeal(true);setDealError('');
        try { await api.put('/customers/'+linkedCustomer.id,changes);await loadRegistryCustomers(); }
        catch(error){setDealError(error?.response?.data?.error || 'Não foi possível atualizar o cadastro compartilhado.');return;}
        finally{setSavingDeal(false);}
      }
    }

    const value = Number(form.value);

    const baseDeal = {
      name: trimmedName,
      company: trimmedCompany, customerId: linkedCustomer?.id || null,
      email: form.email.trim(),
      segment: form.segment,
      owner: form.owner.trim() || 'Não informado',
      value: Number.isFinite(value) ? value : 0,
      stage: form.stage,
      probability: getDefaultProbability(form.stage),
      cycleDays: Number.isFinite(Number(form.cycleDays)) && Number(form.cycleDays) > 0 ? Number(form.cycleDays) : null,
      nextStep: form.nextStep.trim(),
      nextStepDate: form.nextStepDate || null,
      notes: form.notes.trim(),
      updatedAt: new Date().toISOString(),
    };

    if (editingDealId) {
      setDeals((prev) =>
        prev.map((deal) =>
          deal.id === editingDealId
            ? {
                ...deal,
                ...baseDeal,
              }
            : deal,
        ),
      );
    } else {
      const newDeal = {
        id: `OPP-${Date.now()}`,
        ...baseDeal,
        createdAt: new Date().toISOString(),
      };

      setDeals((prev) => [...prev, newDeal]);
    }

    closeModal();
  };

  const handleDeleteDeal = (dealId) => {
    const deal = deals.find((item) => item.id === dealId);
    if (!deal) {
      return;
    }

    const shouldDelete = window.confirm(
      `Deseja excluir a oportunidade "${deal.name}"? Esta ação não pode ser desfeita.`,
    );

    if (!shouldDelete) {
      return;
    }

    setDeals((prev) => prev.filter((item) => item.id !== dealId));
    if (editingDealId === dealId) {
      closeModal();
    }
  };

  const syncIntegrationNow = async (integrationId) => {
    const integration = socialIntegrations.find((item) => item.id === integrationId);
    if (!integration?.connected || !integration.messagingSupported) return;
    setIntegrationBusy(integrationId);
    setIntegrationError('');
    setIntegrationNotice('');
    try {
      await api.post('/crm/channels/connections/' + integrationId + '/validate');
      await loadChannelConnections(true);
      setIntegrationNotice(integration.name + ' validado com sucesso.');
    } catch (err) {
      setIntegrationError(
        err.response?.data?.error || 'Não foi possível validar a conexão.',
      );
      await loadChannelConnections(true);
    } finally {
      setIntegrationBusy('');
    }
  };

  const loadWebhookConfiguration = async (integrationId) => {
    if (!integrationId) {
      setWebhookConfiguration(null);
      return;
    }
    try {
      const response = await api.get(
        '/crm/channels/connections/' + integrationId + '/webhook',
      );
      setWebhookConfiguration(response.data || null);
    } catch {
      setWebhookConfiguration(null);
    }
  };

  const openIntegrationModal = (integrationId) => {
    const integration = socialIntegrations.find((item) => item.id === integrationId);
    if (!integration || !integration.messagingSupported) return;

    setIntegrationError('');
    setIntegrationNotice('');
    setIntegrationModal({ open: true, integrationId });
    setIntegrationForm({
      ...EMPTY_INTEGRATION_FORM,
      accountName: integration.connectionDetails?.accountName || '',
      loginEmail: integration.connectionDetails?.loginEmail || '',
      externalAccountId: integration.connectionDetails?.externalAccountId || '',
    });
    if (integration.connected) loadWebhookConfiguration(integrationId);
    else setWebhookConfiguration(null);
  };

  const closeIntegrationModal = () => {
    setIntegrationModal({ open: false, integrationId: null });
    setIntegrationForm({ ...EMPTY_INTEGRATION_FORM });
    setWebhookConfiguration(null);
    setIntegrationError('');
    setIntegrationNotice('');
  };

  const handleIntegrationFormChange = (event) => {
    const { name, value } = event.target;
    setIntegrationForm((prev) => ({ ...prev, [name]: value }));
  };

  const openProviderAuth = (integrationId) => {
    const integration = socialIntegrations.find((item) => item.id === integrationId);
    if (!integration?.loginUrl || typeof window === 'undefined') {
      return;
    }

    window.open(integration.loginUrl, '_blank', 'noopener,noreferrer');
  };

  const submitIntegrationConnection = async (event) => {
    event.preventDefault();
    const integrationId = integrationModal.integrationId;
    if (!integrationId) return;

    const payload = {
      accountName: integrationForm.accountName.trim(),
      loginEmail: integrationForm.loginEmail.trim(),
      externalAccountId: integrationForm.externalAccountId.trim(),
      accessToken: integrationForm.accessToken.trim(),
      webhookSecret: integrationForm.webhookSecret.trim(),
    };
    if (
      !payload.accountName ||
      !payload.externalAccountId ||
      !payload.accessToken ||
      !payload.webhookSecret
    ) {
      setIntegrationError('Preencha os dados obrigatórios da conexão.');
      return;
    }

    setIntegrationBusy('save');
    setIntegrationError('');
    setIntegrationNotice('');
    try {
      await api.put('/crm/channels/connections/' + integrationId, payload);
      await loadChannelConnections(true);
      await loadWebhookConfiguration(integrationId);
      setIntegrationNotice(
        'Conexão salva e validada. Configure o webhook abaixo para receber mensagens em tempo real.',
      );
    } catch (err) {
      setIntegrationError(
        err.response?.data?.error || 'Não foi possível salvar a conexão.',
      );
      await loadChannelConnections(true);
    } finally {
      setIntegrationBusy('');
    }
  };

  const disconnectIntegration = async (integrationId) => {
    const integration = socialIntegrations.find((item) => item.id === integrationId);
    if (!integration?.connected) return;

    const shouldDisconnect = window.confirm(
      'Deseja desconectar o canal ' + integration.name + '? O histórico já recebido será mantido.',
    );
    if (!shouldDisconnect) return;

    setIntegrationBusy(integrationId);
    setIntegrationError('');
    try {
      await api.delete('/crm/channels/connections/' + integrationId);
      await loadChannelConnections(true);
      setIntegrationNotice(integration.name + ' desconectado.');
      if (integrationModal.integrationId === integrationId) closeIntegrationModal();
    } catch (err) {
      setIntegrationError(
        err.response?.data?.error || 'Não foi possível desconectar o canal.',
      );
    } finally {
      setIntegrationBusy('');
    }
  };

  const formatSyncDate = (value) => {
    if (!value) return 'Nunca sincronizado';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Nunca sincronizado';
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isEditingDeal = Boolean(editingDealId);
  const activeIntegration = integrationModal.integrationId
    ? socialIntegrations.find((item) => item.id === integrationModal.integrationId)
    : null;
  const isManagingIntegration = Boolean(activeIntegration?.connected);
  const overviewTabContent = (
    <div className="space-y-8">
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        {summaryCards.map((card) => (
          <article
            key={card.id}
            className={`flex min-h-[168px] flex-col justify-between rounded-3xl border border-slate-200 bg-gradient-to-br ${card.accent} p-6 shadow-sm ring-1 ring-slate-100 transition hover:-translate-y-0.5 hover:shadow-lg`}
          >
            <div className="flex items-center justify-between">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-xl shadow-sm">
                {card.icon}
              </span>
              <InfoHint label={card.info} />
            </div>
            <div className="mt-6 space-y-1">
              <p className="text-sm font-semibold text-slate-500">{card.label}</p>
              <p className="text-3xl font-bold text-slate-950">
                {typeof card.value === 'number' ? card.value : card.value}
              </p>
              <p className="text-sm font-medium text-slate-500">{card.detail}</p>
            </div>
          </article>
        ))}
      </section>

      <section className="grid gap-6 xl:grid-cols-3">
        <article className="xl:col-span-2 rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <header className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-slate-900">Agenda de relacionamento</h3>
              <p className="text-sm text-slate-500">
                Próximas ações.
              </p>
            </div>
            <span className="rounded-full bg-purple-50 px-3 py-1 text-xs font-medium text-purple-600">
              {upcomingActivities.length} próximos passos
            </span>
          </header>

          <div className="mt-5 space-y-3">
            {upcomingActivities.length ? (
              upcomingActivities.map((deal) => (
                <div
                  key={deal.id}
                  className="flex items-start justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"
                >
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {deal.company} • {deal.name}
                    </p>
                    <p className="text-xs text-slate-500">
                      {deal.nextStep || 'Defina o próximo passo para manter o ritmo da negociação.'}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-2">
                        <FaUserTie className="text-slate-400" /> {deal.owner}
                      </span>
                      <span className="inline-flex items-center gap-2">
                        <FaClock className="text-slate-400" /> {formatDate(deal.nextStepDate)}
                      </span>
                      <span className="inline-flex items-center gap-2">
                        <FaCheckCircle className="text-slate-400" /> {PIPELINE_STAGE_MAP[deal.stage]?.label}
                      </span>
                    </div>
                  </div>
                  <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-500">
                    {formatDaysAgo(deal.updatedAt)}
                  </span>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                Agenda livre.
              </div>
            )}
          </div>
        </article>

        <article className="space-y-4 rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <header className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Saúde do funil</h3>
              <p className="text-sm text-slate-500">Etapas e gargalos.</p>
            </div>
            <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500">
              <FaLayerGroup /> {pipelineStageHealth.reduce((sum, stage) => sum + stage.count, 0)} deals
            </span>
          </header>

          <div className="space-y-3">
            {pipelineStageHealth.map((stage) => (
              <div key={stage.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center justify-between text-sm font-medium text-slate-700">
                  <span>{stage.label}</span>
                  <span>{stage.count} oportunidades</span>
                </div>
                <div className="mt-2 h-2 w-full rounded-full bg-white">
                  <div
                    className={`h-2 rounded-full ${PIPELINE_STAGE_MAP[stage.id]?.progressClass || 'bg-sky-400'}`}
                    style={{ width: `${Math.min(100, stage.avgProbability)}%` }}
                  />
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                  <span>Valor: {formatCurrency(stage.totalValue)}</span>
                  <span>Prob. média: {stage.avgProbability}%</span>
                  <span>Atrasos: {stage.overdue}</span>
                  <span>Sem próxima ação: {stage.withoutNextStep}</span>
                </div>
              </div>
            ))}
          </div>
        </article>
      </section>
      <section className="grid gap-6 lg:grid-cols-2 xl:grid-cols-3">
        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm xl:col-span-1">
          <header className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Oportunidades em foco</h3>
              <p className="text-sm text-slate-500">Mais quentes.</p>
            </div>
            <span className="rounded-full bg-sky-50 px-3 py-1 text-xs font-medium text-sky-600">Top {focusDeals.length}</span>
          </header>

          <div className="mt-4 space-y-3">
            {focusDeals.length ? (
              focusDeals.map((deal) => (
                <div key={deal.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{deal.name}</p>
                      <p className="text-xs text-slate-500">{deal.company}</p>
                    </div>
                    <span className="text-sm font-semibold text-slate-600">{formatCurrency(deal.value)}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-2">
                      <FaUserTie className="text-slate-400" /> {deal.owner}
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <FaCheckCircle className="text-slate-400" />
                      {PIPELINE_STAGE_MAP[deal.stage]?.label}
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <FaChartLine className="text-slate-400" /> {Math.round((deal.probability ?? 0) * 100)}%
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                Cadastre oportunidades.
              </div>
            )}
          </div>
        </article>

        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <header className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Risco de estagnação</h3>
              <p className="text-sm text-slate-500">Sem movimento.</p>
            </div>
            <span className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-600">
              <FaExclamationTriangle /> {staleDeals.length}
            </span>
          </header>

          <div className="mt-4 space-y-3">
            {staleDeals.length ? (
              staleDeals.map((deal) => (
                <div key={deal.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{deal.name}</p>
                      <p className="text-xs text-slate-500">{deal.company}</p>
                    </div>
                    <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-500">
                      {formatDaysAgo(deal.updatedAt)}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-2">
                      <FaUserTie className="text-slate-400" /> {deal.owner}
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <FaCheckCircle className="text-slate-400" /> {PIPELINE_STAGE_MAP[deal.stage]?.label}
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <FaCalendarCheck className="text-slate-400" /> {formatDate(deal.nextStepDate)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                Sem alertas.
              </div>
            )}
          </div>
        </article>

        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <header className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Ganhos recentes</h3>
              <p className="text-sm text-slate-500">Vitórias recentes.</p>
            </div>
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-600">
              <FaTrophy /> {recentWins.length}
            </span>
          </header>

          <div className="mt-4 space-y-3">
            {recentWins.length ? (
              recentWins.map((deal) => (
                <div key={deal.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{deal.company}</p>
                      <p className="text-xs text-slate-500">{deal.name}</p>
                    </div>
                    <span className="text-sm font-semibold text-emerald-600">{formatCurrency(deal.value)}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-2">
                      <FaUserTie className="text-slate-400" /> {deal.owner}
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <FaCalendarCheck className="text-slate-400" /> {formatDate(deal.updatedAt || deal.createdAt)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                Sem vitórias.
              </div>
            )}
          </div>
        </article>
      </section>

      <section className="grid gap-6 lg:grid-cols-[1.5fr,1fr]">
        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <header className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Previsão por trimestre</h3>
              <p className="text-sm text-slate-500">Receita provável.</p>
            </div>
            <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
              <FaChartLine /> {formatCurrency(weightedPipelineValue)}
            </span>
          </header>

          <div className="mt-4 space-y-3">
            {forecastByQuarter.length ? (
              forecastByQuarter.map((item) => (
                <div key={item.label} className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
                  <div className="space-y-1">
                    <p className="font-semibold text-slate-900">{item.label}</p>
                    <p className="text-xs text-slate-500">{item.deals} oportunidades planejadas</p>
                  </div>
                  <div className="text-right text-sm text-slate-600">
                    <p>Valor total: {formatCurrency(item.total)}</p>
                    <p className="text-xs text-slate-500">Previsão: {formatCurrency(item.weighted)}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                Informe datas.
              </div>
            )}
          </div>
        </article>

        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <header className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Cobertura por segmento</h3>
              <p className="text-sm text-slate-500">Distribuição das oportunidades por mercado alvo.</p>
            </div>
            <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
              <FaChartPie /> {coverageBySegment.length} segmentos
            </span>
          </header>

          <div className="mt-4 space-y-3">
            {coverageBySegment.length ? (
              coverageBySegment.map((segment) => (
                <div key={segment.segment} className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
                  <div>
                    <p className="font-semibold text-slate-900">{segment.segment}</p>
                    <p className="text-xs text-slate-500">{segment.count} oportunidades</p>
                  </div>
                  <p className="text-sm font-medium text-slate-600">{formatCurrency(segment.value)}</p>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                Nenhuma oportunidade encontrada com os filtros aplicados.
              </div>
            )}
          </div>
        </article>
      </section>
    </div>
  );
  const pipelineTabContent = (
    <div className="space-y-8">
      <section className="grid gap-4 md:grid-cols-3">
        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Cobertura do funil</h3>
            </div>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
              {activeDealsCount} oportunidades
            </span>
          </div>
          <p className="mt-4 text-3xl font-semibold text-slate-900">{formatCurrency(pipelineValue)}</p>
        </article>

        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Alertas operacionais</h3>
            </div>
            <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-600">
              {stageAlerts.length} alertas
            </span>
          </div>
          <ul className="mt-4 space-y-2 text-sm text-slate-600">
            {stageAlerts.length ? (
              stageAlerts.map((stage) => (
                <li key={stage.id} className="rounded-2xl bg-slate-50 px-4 py-2">
                  {stage.label}: {stage.overdue} atrasos • {stage.withoutNextStep} sem próximo passo
                </li>
              ))
            ) : (
              <li className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-center text-sm text-slate-500">
                Nenhum alerta com os filtros atuais.
              </li>
            )}
          </ul>
        </article>

        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Desempenho recente</h3>
            </div>
            <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-600">
              {formatCurrency(wonThisMonthValue)} ganhos
            </span>
          </div>
          <div className="mt-4 grid gap-2 text-sm text-slate-600">
            <span>Novas oportunidades: {dealsCreatedThisMonth}</span>
            <span>Taxa de conversão geral: {wonRatio}%</span>
            <span>Ciclo médio: {averageCycle ? `${averageCycle} dias` : 'Cadastre mais dados'}</span>
          </div>
        </article>
      </section>

      <section className="space-y-6">
        <header className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-1">
            <h2 className="text-xl font-semibold text-slate-900">Funil de vendas</h2>
          </div>
          <p className="text-sm font-medium text-slate-500">
            Valor potencial em negociação: <span className="text-slate-900">{formatCurrency(pipelineValue)}</span>
          </p>
        </header>

        <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
          {pipelineColumns.map((column) => (
            <div
              key={column.id}
              onDragOver={handleColumnDragOver}
              onDrop={(event) => handleColumnDrop(event, column.id)}
              className={`flex min-h-[420px] flex-col rounded-3xl border bg-white/90 p-4 ${column.borderClass} shadow-sm transition ${
                draggedDealId ? 'ring-2 ring-sky-100' : ''
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">{column.label}</h3>
                  {column.description ? <p className="mt-1 text-xs text-slate-500">{column.description}</p> : null}
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-medium ${column.badgeClass}`}>
                  {formatCurrency(column.totalValue)}
                </span>
              </div>

              <div className="mt-4 space-y-3">
                {column.deals.length ? (
                  column.deals.map((deal) => (
                    <article
                      key={deal.id}
                      draggable
                      onDragStart={(event) => handleDealDragStart(event, deal.id)}
                      onDragEnd={handleDealDragEnd}
                      className={`cursor-grab rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-inner transition active:cursor-grabbing ${
                        draggedDealId === deal.id ? 'scale-[0.98] opacity-60 ring-2 ring-sky-200' : 'hover:-translate-y-0.5 hover:shadow-md'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h4 className="text-sm font-semibold text-slate-900">{deal.name}</h4>
                          <p className="text-xs text-slate-500">{deal.company}</p>
                        </div>
                        <span className="text-sm font-semibold text-slate-600">{formatCurrency(deal.value)}</span>
                      </div>

                      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-2">
                          <FaUserTie className="text-slate-400" />
                          {deal.owner}
                        </span>
                        <span className="inline-flex items-center gap-2">
                          <FaCalendarCheck className="text-slate-400" />
                          {formatDate(deal.nextStepDate)}
                        </span>
                      </div>

                      <div className="mt-3">
                        <label className="text-xs font-medium text-slate-500" htmlFor={`stage-${deal.id}`}>
                          Etapa atual
                        </label>
                        <select
                          id={`stage-${deal.id}`}
                          value={deal.stage}
                          onChange={(event) => handleStageChange(deal.id, event.target.value)}
                          className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                        >
                          {PIPELINE_STAGES.map((stage) => (
                            <option key={stage.id} value={stage.id}>
                              {stage.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {deal.nextStep ? (
                        <p className="mt-3 inline-flex w-full items-start gap-2 rounded-xl bg-white px-3 py-2 text-xs text-slate-600 shadow-sm">
                          <FaStickyNote className="mt-0.5 text-slate-400" /> {deal.nextStep}
                        </p>
                      ) : null}

                      <div className="mt-3">
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span>Probabilidade</span>
                          <span>{Math.round((deal.probability ?? 0) * 100)}%</span>
                        </div>
                        <div className="mt-1 h-2 w-full rounded-full bg-slate-200">
                          <div
                            className={`h-2 rounded-full ${PIPELINE_STAGE_MAP[deal.stage]?.progressClass || 'bg-sky-400'}`}
                            style={{ width: `${Math.min(100, Math.round((deal.probability ?? 0) * 100))}%` }}
                          />
                        </div>
                      </div>

                      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs">
                        <span className="text-slate-400">Atualizado {formatDateTime(deal.updatedAt)}</span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openEditModal(deal)}
                            className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 font-medium text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
                          >
                            <FaEdit /> Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteDeal(deal.id)}
                            className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-white px-3 py-1 font-medium text-rose-500 transition hover:border-rose-300 hover:bg-rose-50"
                          >
                            <FaTrash /> Excluir
                          </button>
                        </div>
                      </div>
                    </article>
                  ))
                ) : (
                  <button
                    type="button"
                    onClick={() => openModal(column.id)}
                    aria-label={`Criar nova oportunidade em ${column.label}`}
                    className="flex min-h-[96px] w-full items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-slate-400 transition hover:border-sky-300 hover:bg-sky-50 hover:text-sky-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-200"
                  >
                    <FaPlus className="text-2xl" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
  const accountsTabContent = (
    <div className="space-y-8">
      <section className="grid gap-6 lg:grid-cols-[2fr,1fr]">
        <article className="overflow-hidden rounded-3xl border border-slate-200 bg-white/90 shadow-sm">
          <header className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Contas estratégicas</h3>
              <p className="text-sm text-slate-500">Empresas-chave.</p>
            </div>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
              {accountSummaries.length} contas
            </span>
          </header>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100 text-left text-sm text-slate-600">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="px-6 py-3 font-medium">Conta</th>
                  <th className="px-6 py-3 font-medium">Segmento</th>
                  <th className="px-6 py-3 font-medium">Valor potencial</th>
                  <th className="px-6 py-3 font-medium">Responsáveis</th>
                  <th className="px-6 py-3 font-medium">Próximo passo</th>
                  <th className="px-6 py-3 font-medium">Taxa de ganho</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {accountSummaries.length ? (
                  accountSummaries.map((account) => (
                    <tr key={account.company}>
                      <td className="px-6 py-4 text-slate-900">
                        <div className="font-semibold">{account.company}</div>
                        <div className="text-xs text-slate-500">{account.opportunities} oportunidades</div>
                      </td>
                      <td className="px-6 py-4">{account.segment}</td>
                      <td className="px-6 py-4 font-medium text-slate-700">{formatCurrency(account.totalValue)}</td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                          {account.owners.length ? account.owners.join(', ') : 'Não informado'}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-xs text-slate-500">
                        {account.nextStepDate ? formatDate(account.nextStepDate) : 'Sem data definida'}
                      </td>
                      <td className="px-6 py-4 font-medium text-slate-700">{account.winRate}%</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-sm text-slate-500">
                      Nenhuma conta encontrada com os filtros aplicados.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </article>

        <div className="space-y-6">
          <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
            <header className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Performance da equipe</h3>
                <p className="text-sm text-slate-500">Por responsável.</p>
              </div>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                {teamPerformance.length} membros
              </span>
            </header>

            <div className="mt-4 space-y-3">
              {teamPerformance.length ? (
                teamPerformance.map((member) => (
                  <div key={member.owner} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{member.owner}</p>
                        <p className="text-xs text-slate-500">
                          {member.opportunities} oportunidades • {formatCurrency(member.totalValue)}
                        </p>
                      </div>
                      <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-600">
                        {member.winRate}% win rate
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                      <span>Ganhos: {formatCurrency(member.wonValue)}</span>
                      <span>Ciclo médio: {member.avgCycle ? `${member.avgCycle} dias` : '—'}</span>
                      <span>Próximos 7 dias: {member.upcoming}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                  Aplique filtros menos restritivos para visualizar o desempenho da equipe.
                </div>
              )}
            </div>
          </article>

          <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900">Agenda crítica</h3>
            <p className="text-sm text-slate-500">Prioridades.</p>
            <div className="mt-4 space-y-3">
              {upcomingActivities.length ? (
                upcomingActivities.slice(0, 5).map((deal) => (
                  <div key={deal.id} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-slate-900">{deal.company}</span>
                      <span className="text-xs text-slate-500">{formatDate(deal.nextStepDate)}</span>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">{deal.nextStep || 'Defina uma próxima ação.'}</p>
                  </div>
                ))
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                  Nenhum compromisso encontrado nos próximos 7 dias.
                </div>
              )}
            </div>
          </article>
        </div>
      </section>
    </div>
  );
  const integrationsTabContent = (
    <div className="space-y-8">
      <section className="grid gap-4 md:grid-cols-3">
        <article className="rounded-3xl border border-sky-100 bg-sky-50/80 p-5 shadow-sm md:col-span-3">
          <div className="flex items-center gap-2">
            <FaPlug className="text-sky-600" />
            <h3 className="text-sm font-extrabold text-slate-900">Conectar canais</h3>
            <InfoHint label="Escolha o canal, autentique no provedor, informe as credenciais e teste a sincronização." />
          </div>
        </article>

        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900">Integrações conectadas</h3>
          <p className="mt-2 text-sm text-slate-500">
            Captação automática.
          </p>
          <div className="mt-4 flex items-center gap-4">
            <div className="text-3xl font-semibold text-slate-900">{connectedIntegrations.length}</div>
            <div className="text-sm text-slate-500">
              de {socialIntegrations.length} canais disponíveis já estão ativos.
            </div>
          </div>
        </article>

        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900">Próxima ação</h3>
          <p className="mt-2 text-sm text-slate-500">
            Automatize ações.
          </p>
          <ul className="mt-4 space-y-2 text-sm text-slate-600">
            <li className="rounded-2xl bg-slate-50 px-4 py-2">• Crie regras por palavra-chave para leads quentes.</li>
            <li className="rounded-2xl bg-slate-50 px-4 py-2">• Envie mensagens urgentes para o responsável certo.</li>
            <li className="rounded-2xl bg-slate-50 px-4 py-2">• Compare performance por canal e ajuste campanhas.</li>
          </ul>
        </article>

        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900">Últimas sincronizações</h3>
          <p className="mt-2 text-sm text-slate-500">Canais ativos.</p>
          <ul className="mt-4 space-y-2 text-sm text-slate-600">
            {socialIntegrations.map((integration) => (
              <li key={integration.id} className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-2">
                <span>{integration.name}</span>
                <span className="text-xs text-slate-500">{formatSyncDate(integration.lastSync)}</span>
              </li>
            ))}
          </ul>
        </article>
      </section>

      {integrationError && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
          {integrationError}
        </div>
      )}
      {integrationNotice && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
          {integrationNotice}
        </div>
      )}

      <section className="grid gap-6 lg:grid-cols-2">
        {socialIntegrations.map((integration) => {
          const Icon = integration.icon;
          return (
            <div
              key={integration.id}
              className="flex flex-col gap-3 rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm lg:flex-row lg:items-start lg:justify-between"
            >
              <div className="flex items-start gap-3">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-xl text-sky-500">
                  <Icon />
                </span>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-slate-900">{integration.name}</p>
                    <InfoHint label={`${integration.description} ${integration.audience}`} />
                  </div>
                  <p className="text-xs text-slate-400">
                    {integration.connected ? 'Conectado' : 'Não conectado'} • {formatSyncDate(integration.lastSync)}
                  </p>
                  {integration.connected && integration.connectionDetails ? (
                    <p className="text-xs text-slate-500">
                      Conta: <span className="font-semibold">{integration.connectionDetails.accountName}</span>
                      {integration.connectionDetails.loginEmail ? ` • ${integration.connectionDetails.loginEmail}` : ''}
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="flex flex-col gap-2 lg:items-end">
                {integration.messagingSupported ? (
                  <button
                    type="button"
                    onClick={() => openIntegrationModal(integration.id)}
                    disabled={integrationBusy === integration.id}
                    className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-xs font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-200 disabled:opacity-50 ${
                      integration.connected
                        ? 'bg-white text-slate-600 shadow-sm hover:bg-slate-100'
                        : 'bg-sky-500 text-white shadow-sm hover:bg-sky-600'
                    }`}
                  >
                    {integration.connected ? 'Gerenciar conexão' : 'Conectar canal'}
                  </button>
                ) : (
                  <span className="max-w-[210px] rounded-2xl bg-slate-100 px-3 py-2 text-center text-[11px] font-semibold leading-relaxed text-slate-500">
                    Mensagens privadas indisponíveis pela API padrão
                  </span>
                )}
                {integration.connected && integration.messagingSupported ? (
                  <div className="flex flex-col gap-2 lg:items-end">
                    <button
                      type="button"
                      onClick={() => syncIntegrationNow(integration.id)}
                      disabled={integrationBusy === integration.id}
                      className="inline-flex items-center justify-center gap-2 rounded-full border border-sky-500 px-4 py-2 text-xs font-medium text-sky-600 transition hover:bg-sky-50 disabled:opacity-50"
                    >
                      <FaSyncAlt className={integrationBusy === integration.id ? 'animate-spin' : ''} />
                      Validar conexão
                    </button>
                    <button
                      type="button"
                      onClick={() => disconnectIntegration(integration.id)}
                      className="inline-flex items-center justify-center rounded-full border border-rose-200 px-4 py-2 text-xs font-medium text-rose-500 transition hover:border-rose-300 hover:bg-rose-50"
                    >
                      Desconectar
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
  const reportsTabContent = (
    <div className="space-y-8">
      <AIInsightsPanel
        module="crm"
        title="Insights inteligentes do CRM"
        subtitle="Análise do funil, oportunidades, previsão ponderada, alertas e integrações comerciais."
        data={{ deals, weightedPipelineValue, wonDealsValue, stageAlerts, connectedIntegrations }}
      />
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Receita prevista</p>
          <p className="mt-3 text-3xl font-semibold text-slate-900">{formatCurrency(weightedPipelineValue)}</p>
          <p className="text-sm text-slate-500">Previsão ponderada.</p>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Ganhos recentes</p>
          <p className="mt-3 text-3xl font-semibold text-slate-900">{formatCurrency(wonDealsValue)}</p>
          <p className="text-sm text-slate-500">Total ganho.</p>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Alertas ativos</p>
          <p className="mt-3 text-3xl font-semibold text-amber-600">{stageAlerts.length}</p>
          <p className="text-sm text-slate-500">Atrasos e pendências.</p>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Integrações</p>
          <p className="mt-3 text-3xl font-semibold text-slate-900">{connectedIntegrations.length}</p>
          <p className="text-sm text-slate-500">Leads automáticos.</p>
        </article>
      </section>

      <section className="grid gap-6 lg:grid-cols-[2fr,1fr]">
        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <header className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Alertas por etapa</h3>
              <p className="text-sm text-slate-500">Ação imediata.</p>
            </div>
            <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-600">
              {stageAlerts.length} alertas
            </span>
          </header>

          <div className="mt-4 space-y-3">
            {stageAlerts.length ? (
              stageAlerts.map((stage) => (
                <div key={stage.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-900">{stage.label}</p>
                    <span className="text-xs text-slate-500">{stage.count} oportunidades</span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                    <span>Atrasos: {stage.overdue}</span>
                    <span>Sem próxima ação: {stage.withoutNextStep}</span>
                    <span>Probabilidade média: {stage.avgProbability}%</span>
                    <span>Valor: {formatCurrency(stage.totalValue)}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                Nenhum alerta identificado – ótimo trabalho!
              </div>
            )}
          </div>
        </article>

        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900">Leads a resgatar</h3>
          <p className="text-sm text-slate-500">Risco alto.</p>
          <div className="mt-4 space-y-3">
            {staleDeals.length ? (
              staleDeals.map((deal) => (
                <div key={deal.id} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-slate-900">{deal.company}</span>
                    <span className="text-xs text-slate-500">{formatDaysAgo(deal.updatedAt)}</span>
                  </div>
                  <p className="text-xs text-slate-500">{deal.name}</p>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                Sem riscos mapeados neste momento.
              </div>
            )}
          </div>
        </article>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900">Desempenho por responsável</h3>
          <p className="text-sm text-slate-500">Por consultor.</p>
          <div className="mt-4 space-y-3">
            {teamPerformance.length ? (
              teamPerformance.map((member) => (
                <div key={member.owner} className="space-y-2 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-900">{member.owner}</span>
                    <span className="text-xs text-slate-500">{formatCurrency(member.totalValue)}</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-white">
                    <div
                      className="h-2 rounded-full bg-sky-500"
                      style={{ width: `${Math.min(100, member.winRate)}%` }}
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                    <span>Win rate: {member.winRate}%</span>
                    <span>Ganhos: {formatCurrency(member.wonValue)}</span>
                    <span>Ciclo médio: {member.avgCycle ? `${member.avgCycle} dias` : '—'}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                Nenhum dado disponível para gerar o relatório da equipe.
              </div>
            )}
          </div>
        </article>

        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900">Vitórias recentes</h3>
          <p className="text-sm text-slate-500">Registre os aprendizados dos negócios concluídos.</p>
          <div className="mt-4 space-y-3">
            {recentWins.length ? (
              recentWins.map((deal) => (
                <div key={deal.id} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-slate-900">{deal.company}</span>
                    <span className="text-xs text-slate-500">{formatDate(deal.updatedAt || deal.createdAt)}</span>
                  </div>
                  <p className="text-xs text-slate-500">{deal.name}</p>
                  <p className="text-xs font-medium text-emerald-600">{formatCurrency(deal.value)}</p>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                Cadastre oportunidades ganhas para alimentar este painel.
              </div>
            )}
          </div>
        </article>
      </section>
    </div>
  );
  return (
    <div className="space-y-10 bg-gradient-to-b from-slate-50 via-white to-white p-6 lg:p-10">
      <header data-crm-header="flat" className="-mx-6 -mt-6 bg-[#071f54] px-6 pb-5 pt-6 shadow-lg lg:-mx-10 lg:-mt-10 lg:px-10 lg:pt-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <span className="grid h-11 w-11 place-items-center rounded-2xl bg-blue-500 text-lg text-white shadow-lg ring-1 ring-white/20" aria-hidden="true">
                <FaHandshake />
              </span>
              <h1 className="text-[1.55rem] font-extrabold leading-tight text-white">CRM</h1>
              <InfoHint label="Kanban simples para organizar oportunidades, próximos passos e canais conectados." />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {openTab && <button type="button" onClick={() => openTab('customers')}
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full border border-white/30 bg-white/10 px-5 py-2 text-sm font-semibold text-white transition hover:bg-white/20">
              <FaUsers/> Clientes
            </button>}
            <button
              type="button"
              onClick={openModal}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-2 text-sm font-extrabold text-[#071f54] shadow-md transition hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
            >
              <FaPlus /> Nova oportunidade
            </button>
          </div>
        </div>

        <nav className="mt-5 flex flex-wrap gap-2">
          {CRM_TABS.map((tab) => {
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 ${
                  activeTab === tab.id
                    ? 'border-white bg-white text-[#071f54] shadow-md'
                    : 'border-white/15 bg-white/10 text-white hover:bg-white/20'
                }`}
                title={tab.hint}
              >
                <TabIcon /> {tab.label}
              </button>
            );
          })}
        </nav>
      </header>

      {!['emailMarketing', 'integrations', 'inbox'].includes(activeTab) && (
        <section className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="relative w-full sm:w-64">
                <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Buscar"
                  className="w-full rounded-full border border-slate-200 bg-slate-50 py-2 pl-11 pr-4 text-sm text-slate-700 shadow-inner focus:border-sky-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-100"
                />
              </div>
              <div className="relative">
                <FaFilter className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <select
                  value={ownerFilter}
                  onChange={(event) => setOwnerFilter(event.target.value)}
                  className="appearance-none rounded-full border border-slate-200 bg-white py-2 pl-9 pr-10 text-sm text-slate-600 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                >
                  <option value="all">Responsáveis</option>
                  {owners.map((owner) => (
                    <option key={owner} value={owner}>
                      {owner}
                    </option>
                  ))}
                </select>
              </div>
              <div className="relative">
                <FaUsers className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <select
                  value={segmentFilter}
                  onChange={(event) => setSegmentFilter(event.target.value)}
                  className="appearance-none rounded-full border border-slate-200 bg-white py-2 pl-10 pr-10 text-sm text-slate-600 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                >
                  <option value="all">Segmentos</option>
                  {segments.map((segment) => (
                    <option key={segment} value={segment}>
                      {segment}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="text-xs text-slate-500">
              {filteredDeals.length}/{deals.length} oportunidades
            </div>
          </div>
        </section>
      )}

      <div className="space-y-10">
        {activeTab === 'overview' && overviewTabContent}
        {activeTab === 'pipeline' && pipelineTabContent}
        {activeTab === 'accounts' && accountsTabContent}
        {activeTab === 'integrations' && integrationsTabContent}
        {activeTab === 'inbox' && <CRMChannelInbox />}
        {activeTab === 'emailMarketing' && <CRMEmailMarketing deals={deals} />}
        {activeTab === 'reports' && reportsTabContent}
      </div>

      <Modal open={integrationModal.open} onClose={closeIntegrationModal}>
        {activeIntegration ? (
          <div className="space-y-6">
            <header className="space-y-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-1">
                  <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-4 py-1 text-xs font-medium text-slate-600">
                    <FaShareAlt /> {activeIntegration.name}
                  </span>
                  <div className="flex items-center gap-2">
                    <h2 className="text-2xl font-semibold text-slate-900">
                      {isManagingIntegration ? 'Gerenciar conexão' : `Conectar ${activeIntegration.name}`}
                    </h2>
                    <InfoHint label={isManagingIntegration
                      ? 'Atualize as credenciais usadas na sincronização.'
                      : 'Autentique no provedor e informe as credenciais para receber leads.'}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => openProviderAuth(activeIntegration.id)}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-medium text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
                >
                  <FaExternalLinkAlt /> Abrir {activeIntegration.name}
                </button>
              </div>
            </header>

            {integrationError && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
                {integrationError}
              </div>
            )}
            {integrationNotice && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
                {integrationNotice}
              </div>
            )}
            {webhookConfiguration && (
              <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
                <div className="text-sm font-bold text-blue-900">Webhook para receber mensagens</div>
                <p className="mt-1 text-xs text-blue-700">
                  Cadastre estes dados no painel da Meta e assine os eventos de mensagens do canal.
                </p>
                <div className="mt-3 space-y-2 text-xs">
                  <div>
                    <span className="font-bold text-slate-600">URL de callback</span>
                    <code className="mt-1 block break-all rounded-lg bg-white px-3 py-2 text-slate-700">
                      {webhookConfiguration.callbackUrl}
                    </code>
                  </div>
                  <div>
                    <span className="font-bold text-slate-600">Token de verificação</span>
                    <code className="mt-1 block break-all rounded-lg bg-white px-3 py-2 text-slate-700">
                      {webhookConfiguration.verifyToken}
                    </code>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={submitIntegrationConnection} className="space-y-5">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label htmlFor="integration-account" className="text-sm font-medium text-slate-700">
                    Nome da conta
                  </label>
                  <input
                    id="integration-account"
                    name="accountName"
                    value={integrationForm.accountName}
                    onChange={handleIntegrationFormChange}
                    placeholder="Ex.: Equipe Comercial"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="integration-email" className="text-sm font-medium text-slate-700">
                    E-mail de login (opcional)
                  </label>
                  <input
                    id="integration-email"
                    name="loginEmail"
                    type="email"
                    value={integrationForm.loginEmail}
                    onChange={handleIntegrationFormChange}
                    placeholder="contato@empresa.com"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <label htmlFor="integration-external-id" className="text-sm font-medium text-slate-700">
                    {activeIntegration.id === 'whatsapp'
                      ? 'Phone Number ID do WhatsApp'
                      : activeIntegration.id === 'instagram'
                        ? 'ID da conta profissional do Instagram'
                        : 'ID da Página do Facebook'}
                  </label>
                  <input
                    id="integration-external-id"
                    name="externalAccountId"
                    value={integrationForm.externalAccountId}
                    onChange={handleIntegrationFormChange}
                    placeholder={activeIntegration.id === 'whatsapp' ? 'Ex.: 123456789012345' : 'ID numérico fornecido pela Meta'}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                    required
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <label htmlFor="integration-token" className="text-sm font-medium text-slate-700">
                    Token de acesso
                  </label>
                  <input
                    id="integration-token"
                    name="accessToken"
                    type="password"
                    autoComplete="new-password"
                    value={integrationForm.accessToken}
                    onChange={handleIntegrationFormChange}
                    placeholder={isManagingIntegration ? 'Informe um token para atualizar a conexão' : 'Cole o token gerado pela Meta'}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                    required
                  />
                  <p className="text-xs text-slate-400">
                    O token é salvo criptografado no servidor e nunca volta para o navegador.
                  </p>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <label htmlFor="integration-app-secret" className="text-sm font-medium text-slate-700">
                    App Secret da aplicação Meta
                  </label>
                  <input
                    id="integration-app-secret"
                    name="webhookSecret"
                    type="password"
                    autoComplete="new-password"
                    value={integrationForm.webhookSecret}
                    onChange={handleIntegrationFormChange}
                    placeholder="Usado para validar a assinatura das mensagens recebidas"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                    required
                  />
                </div>
              </div>

              <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeIntegrationModal}
                  className="inline-flex items-center justify-center rounded-full border border-slate-300 px-5 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={integrationBusy === 'save'}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#071f54] px-6 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 disabled:opacity-50"
                >
                  {integrationBusy === 'save' && <FaSyncAlt className="animate-spin" />}
                  {integrationBusy === 'save'
                    ? 'Validando...'
                    : isManagingIntegration
                      ? 'Salvar e validar'
                      : 'Conectar e validar'}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">Integração não encontrada</h2>
            <p className="text-sm text-slate-500">
              Atualize a página e tente novamente. Se o problema persistir, contate o administrador da plataforma.
            </p>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={closeIntegrationModal}
                className="inline-flex items-center justify-center rounded-full border border-slate-300 px-5 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
              >
                Fechar
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={modalOpen} onClose={closeModal} size="max-w-4xl" showCloseButton={false}>
        <div className="overflow-hidden rounded-3xl bg-slate-50">
          <div className="flex items-center justify-between gap-4 bg-gradient-to-r from-[#071f54] via-[#0b3474] to-[#0d4d91] px-5 py-5 text-white sm:px-6">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 text-xl">
                <FaHandshake />
              </span>
              <h2 className="text-xl font-semibold">
                {isEditingDeal ? 'Editar oportunidade' : 'Nova oportunidade'}
              </h2>
            </div>
            <button
              type="button"
              onClick={closeModal}
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/15 bg-white/10 text-white transition hover:bg-white/20"
              aria-label="Fechar"
            >
              <FaTimes />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5 p-4 sm:p-6">
            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="mb-4 flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><FaUserTie /></span>
                <h3 className="font-semibold text-slate-800">Cliente e oportunidade</h3>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700 md:col-span-2">
                  Nome da oportunidade <span className="text-rose-500">*</span>
                  <input id="crm-name" name="name" value={form.name} onChange={handleFormChange} placeholder="Ex.: Implementação plataforma BPM" className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" required />
                </label>
                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">Cliente compartilhado
                  <select aria-label="Cliente compartilhado" value={form.customerId || ''} onChange={event=>{const customer=registryCustomers.find(item=>String(item.id)===event.target.value);setForm(prev=>({...prev,customerId:event.target.value,...(customer?{company:customer.name,email:customer.email || ''}:{})}));}} className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm">
                    <option value="">Cadastro livre / sem vínculo</option>{registryCustomers.map(customer=><option key={customer.id} value={customer.id}>{customer.name}{customer.taxId?' · '+customer.taxId:''}</option>)}
                  </select>
                  {form.customerId && <span className="text-xs font-normal text-emerald-700">Empresa e e-mail serão atualizados no mesmo cadastro dos módulos integrados.</span>}
                </label>
                {dealError && <p role="alert" className="text-sm text-red-700">{dealError}</p>}
                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                  Empresa <span className="text-rose-500">*</span>
                  <input id="crm-company" name="company" value={form.company} onChange={handleFormChange} placeholder="Ex.: ACME Corp" className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" required />
                </label>
                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                  E-mail
                  <input id="crm-email" name="email" type="email" value={form.email} onChange={handleFormChange} placeholder="cliente@empresa.com" className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
                </label>
                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                  Segmento
                  <select id="crm-segment" name="segment" value={form.segment} onChange={handleFormChange} className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50">
                    {segments.map((segment) => <option key={segment} value={segment}>{segment}</option>)}
                  </select>
                </label>
                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                  Responsável
                  <input id="crm-owner" name="owner" value={form.owner} onChange={handleFormChange} placeholder="Ex.: Ana Oliveira" className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
                </label>
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="mb-4 flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600"><FaMoneyBillWave /></span>
                <h3 className="font-semibold text-slate-800">Negociação</h3>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                  Valor estimado (R$)
                  <input id="crm-value" name="value" type="number" min="0" step="500" value={form.value} onChange={handleFormChange} placeholder="Ex.: 25000" className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-50" />
                </label>
                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                  Ciclo estimado (dias)
                  <input id="crm-cycle" name="cycleDays" type="number" min="0" value={form.cycleDays} onChange={handleFormChange} placeholder="Ex.: 30" className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-50" />
                </label>
              </div>
              <div className="mt-4">
                <p className="mb-2 text-sm font-semibold text-slate-700">Estágio</p>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
                  {PIPELINE_STAGES.map((stage) => {
                    const selectedStage = form.stage === stage.id;
                    return (
                      <button
                        key={stage.id}
                        type="button"
                        onClick={() => handleFormChange({ target: { name: 'stage', value: stage.id } })}
                        className={`rounded-xl border px-3 py-3 text-left text-xs font-bold transition ${
                          selectedStage ? `${stage.borderClass} ${stage.badgeClass} ring-2 ring-blue-50` : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
                        }`}
                      >
                        {stage.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="mb-4 flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-600"><FaCalendarCheck /></span>
                <h3 className="font-semibold text-slate-800">Próxima ação</h3>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                  Próximo passo
                  <input id="crm-next-step" name="nextStep" value={form.nextStep} onChange={handleFormChange} placeholder="Ex.: Enviar proposta atualizada" className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-50" />
                </label>
                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                  Data
                  <input id="crm-next-step-date" name="nextStepDate" type="date" value={form.nextStepDate} onChange={handleFormChange} className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-50" />
                </label>
                <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700 md:col-span-2">
                  Observações
                  <textarea id="crm-notes" name="notes" value={form.notes} onChange={handleFormChange} rows={4} placeholder="Registre aprendizados, objeções ou recursos prometidos ao cliente." className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-50" />
                </label>
              </div>
            </section>

            <div className="sticky bottom-0 -mx-4 flex flex-wrap justify-end gap-2 border-t border-slate-200 bg-white/95 px-4 py-4 backdrop-blur sm:-mx-6 sm:px-6">
              <button type="button" onClick={closeModal} className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancelar</button>
              <button type="submit" disabled={savingDeal} className="inline-flex items-center gap-2 rounded-xl bg-[#0b3474] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0a2c62]">
                <FaCheckCircle />
                {isEditingDeal ? 'Atualizar oportunidade' : 'Salvar oportunidade'}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
}
